import { BoatState, WindState, PointOfSail, Vector2D } from '../types';
import { BOAT_CONFIG } from '../data/boatConfig';
import {
  addVectors,
  scaleVector,
  magnitude,
  normalizeAngle,
  clamp,
  lerp,
} from './geometry';

export function calculateApparentWind(
  trueWindDirection: number,
  trueWindSpeed: number,
  boatHeading: number,
  boatVelocity: Vector2D
): { apparentWindAngle: number; apparentWindSpeed: number } {
  const trueWindVector = {
    x: -Math.sin(trueWindDirection) * trueWindSpeed,
    y: Math.cos(trueWindDirection) * trueWindSpeed,
  };

  const apparentWindVector = {
    x: trueWindVector.x - boatVelocity.x,
    y: trueWindVector.y - boatVelocity.y,
  };

  const apparentWindSpeed = magnitude(apparentWindVector);

  const windAngle = Math.atan2(apparentWindVector.x, -apparentWindVector.y);
  let apparentWindAngle = normalizeAngle(windAngle - boatHeading);

  return { apparentWindAngle, apparentWindSpeed };
}

export function classifyPointOfSail(apparentWindAngle: number): PointOfSail {
  const absAngle = Math.abs(apparentWindAngle);
  const degrees = absAngle * (180 / Math.PI);

  if (degrees < 45) return 'inIrons';
  if (degrees < 60) return 'closeHauled';
  if (degrees < 80) return 'closeReach';
  if (degrees < 100) return 'beamReach';
  if (degrees < 150) return 'broadReach';
  return 'running';
}

export function calculateSailForce(
  apparentWindAngle: number,
  apparentWindSpeed: number,
  boatHeading: number
): Vector2D {
  const absAngle = Math.abs(apparentWindAngle);

  let forceCoefficient: number;

  if (absAngle < Math.PI / 4) {
    forceCoefficient = 0;
  } else if (absAngle < Math.PI / 3) {
    const t = (absAngle - Math.PI / 4) / (Math.PI / 12);
    forceCoefficient = 0.3 + t * 0.3;
  } else if (absAngle < Math.PI / 2) {
    const t = (absAngle - Math.PI / 3) / (Math.PI / 6);
    forceCoefficient = 0.6 + t * 0.4;
  } else if (absAngle < (3 * Math.PI) / 4) {
    forceCoefficient = 1.0;
  } else {
    const t = (absAngle - (3 * Math.PI) / 4) / (Math.PI / 4);
    forceCoefficient = 1.0 - t * 0.3;
  }

  const forceMagnitude =
    forceCoefficient *
    BOAT_CONFIG.sailArea *
    BOAT_CONFIG.liftCoefficient *
    apparentWindSpeed *
    apparentWindSpeed *
    0.0008;

  return {
    x: Math.sin(boatHeading) * forceMagnitude,
    y: -Math.cos(boatHeading) * forceMagnitude,
  };
}

export function calculateDrag(velocity: Vector2D): Vector2D {
  const speed = magnitude(velocity);
  if (speed < 0.01) return { x: 0, y: 0 };

  const dragMagnitude = speed * speed * 0.01;
  const direction = { x: -velocity.x / speed, y: -velocity.y / speed };

  return scaleVector(direction, dragMagnitude);
}

export function calculateTurnRate(rudderAngle: number, speed: number): number {
  if (speed < BOAT_CONFIG.minSteerageSpeed) {
    return rudderAngle * BOAT_CONFIG.turningMoment * 0.2;
  }

  const speedFactor = Math.min(speed / 3, 1.0);
  const rudderEffectiveness = Math.cos(rudderAngle * 0.5);

  return rudderAngle * BOAT_CONFIG.turningMoment * speedFactor * rudderEffectiveness;
}

export function calculateHeelAngle(
  apparentWindSpeed: number,
  apparentWindAngle: number,
  currentHeel: number
): number {
  const crossWindComponent = Math.sin(apparentWindAngle);
  const windPressure = apparentWindSpeed * apparentWindSpeed * 0.005;

  const targetHeel = clamp(
    crossWindComponent * windPressure,
    -BOAT_CONFIG.maxHeelAngle,
    BOAT_CONFIG.maxHeelAngle
  );

  return lerp(currentHeel, targetHeel, BOAT_CONFIG.heelDamping);
}

export function calculateOptimalSailAngle(apparentWindAngle: number): number {
  const absAngle = Math.abs(apparentWindAngle);

  if (absAngle < Math.PI / 4) {
    return 0;
  }

  const optimalAngle = (absAngle - Math.PI / 4) * 0.5;
  return Math.min(optimalAngle, Math.PI / 2);
}

export function updateRudder(
  currentAngle: number,
  input: number,
  deltaTime: number
): number {
  const targetAngle = input * BOAT_CONFIG.maxRudderAngle;
  const diff = targetAngle - currentAngle;
  const maxChange = BOAT_CONFIG.rudderTurnRate * deltaTime * 60;

  if (Math.abs(diff) < maxChange) {
    return targetAngle;
  }

  return currentAngle + Math.sign(diff) * maxChange;
}

export function updateBoatPhysics(
  boat: BoatState,
  wind: WindState,
  deltaTime: number
): BoatState {
  const newRudderAngle = updateRudder(boat.rudderAngle, boat.rudderInput, deltaTime);

  const { apparentWindAngle, apparentWindSpeed } = calculateApparentWind(
    wind.direction,
    wind.speed * wind.gustFactor,
    boat.heading,
    boat.velocity
  );

  const pointOfSail = classifyPointOfSail(apparentWindAngle);
  const inNoGoZone = Math.abs(apparentWindAngle) < BOAT_CONFIG.noGoZoneHalfAngle;

  const sailForce = inNoGoZone
    ? { x: 0, y: 0 }
    : calculateSailForce(apparentWindAngle, apparentWindSpeed, boat.heading);

  const dragForce = calculateDrag(boat.velocity);

  const netForce = addVectors(sailForce, dragForce);
  const acceleration = scaleVector(netForce, 1 / BOAT_CONFIG.mass);

  let newVelocity = addVectors(
    boat.velocity,
    scaleVector(acceleration, deltaTime * 60)
  );

  newVelocity = scaleVector(newVelocity, BOAT_CONFIG.dragCoefficient);

  const speed = magnitude(newVelocity);
  if (speed > BOAT_CONFIG.maxSpeed) {
    newVelocity = scaleVector(newVelocity, BOAT_CONFIG.maxSpeed / speed);
  }

  const turnRate = calculateTurnRate(newRudderAngle, speed);
  const newHeading = normalizeAngle(boat.heading + turnRate * deltaTime * 60);

  const newPosition = addVectors(
    boat.position,
    scaleVector(newVelocity, deltaTime * 60)
  );

  const newHeelAngle = calculateHeelAngle(
    apparentWindSpeed,
    apparentWindAngle,
    boat.heelAngle
  );

  const optimalSailAngle = calculateOptimalSailAngle(apparentWindAngle);

  return {
    ...boat,
    position: newPosition,
    velocity: newVelocity,
    heading: newHeading,
    speed: speed * 2,
    rudderAngle: newRudderAngle,
    heelAngle: newHeelAngle,
    pointOfSail,
    apparentWindAngle,
    apparentWindSpeed,
    optimalSailAngle,
    sailAngle: optimalSailAngle * Math.sign(apparentWindAngle),
  };
}
